import { useCallback, useEffect, useRef, useState } from "react";
import type {
  CrmMessage,
  CrmState,
  CrmRouteChangePayload,
  CrmErrorPayload,
  CrmNavigateParentPayload,
} from "./types";
import { CRM_MESSAGES } from "./types";
import { useAuthStore } from "@/lib/store/authStore";
import { trackEvent } from "@/lib/activity-tracker/tracker";

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

interface UseCrmMessagingOptions {
  onReady?: () => void;
  onRouteChange?: (path: string, title: string) => void;
  onError?: (error: string) => void;
  onNavigateParent?: (action: string) => void;
}

const REQUEST_TIMEOUT_MS = 10000; // 10 seconds

// Calculate the Frappe origin for cross-origin iframe communication
// GOTCHA G2: Must match BuilderEmbed exactly
// In control_plane mode, use tenant server URL instead of Frappe origin
function getFrappeOrigin(authMode: string | undefined, tenantServerUrl: string | null): string {
  // In control_plane mode, messages come from tenant server
  if (authMode === 'control_plane' && tenantServerUrl) {
    return new URL(tenantServerUrl).origin;
  }

  const envUrl = import.meta.env.VITE_FRAPPE_URL;
  if (envUrl) {
    return new URL(envUrl).origin;
  }
  // In development, Frappe runs on port 8000
  if (window.location.port === "5173") {
    return `${window.location.protocol}//${window.location.hostname}:8000`;
  }
  return window.location.origin;
}

export function useCrmMessaging(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  options: UseCrmMessagingOptions
) {
  const [isReady, setIsReady] = useState(false);
  const pendingRequests = useRef<Map<string, PendingRequest>>(new Map());
  const requestIdCounter = useRef(0);

  // Get auth mode and tenant server URL for origin calculation
  const authMode = useAuthStore((state) => state.authMode);
  const tenantServerUrl = useAuthStore((state) => state.tenantServerUrl);
  const frappeOriginRef = useRef(getFrappeOrigin(authMode, tenantServerUrl));

  // Update origin ref when auth mode or tenant server URL changes
  useEffect(() => {
    frappeOriginRef.current = getFrappeOrigin(authMode, tenantServerUrl);
  }, [authMode, tenantServerUrl]);

  // GOTCHA G4: Store options in ref to avoid stale closures
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Generate unique request ID
  const generateRequestId = useCallback(() => {
    return `req_${++requestIdCounter.current}_${Date.now()}`;
  }, []);

  // Send message to iframe
  const sendMessage = useCallback(
    (type: string, payload?: unknown, requestId?: string) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) {
        console.warn("[CrmEmbed] iframe not available");
        return;
      }

      const message: CrmMessage = { type, payload, requestId };
      iframe.contentWindow.postMessage(message, frappeOriginRef.current);
    },
    [iframeRef]
  );

  // Send message and wait for response
  const sendRequest = useCallback(
    <T = unknown>(type: string, payload?: unknown): Promise<T> => {
      return new Promise((resolve, reject) => {
        const requestId = generateRequestId();

        const timeout = setTimeout(() => {
          pendingRequests.current.delete(requestId);
          reject(
            new Error(`Request ${type} timed out after ${REQUEST_TIMEOUT_MS}ms`)
          );
        }, REQUEST_TIMEOUT_MS);

        pendingRequests.current.set(requestId, {
          resolve: resolve as (value: unknown) => void,
          reject,
          timeout,
        });
        sendMessage(type, payload, requestId);
      });
    },
    [sendMessage, generateRequestId]
  );

  // Handle incoming messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Security: Only accept messages from Frappe origin
      if (event.origin !== frappeOriginRef.current) {
        return;
      }

      const data = event.data as CrmMessage;
      if (!data || typeof data.type !== "string") {
        return;
      }

      const { type, payload, requestId } = data;

      // Handle response to pending request FIRST
      if (requestId && pendingRequests.current.has(requestId)) {
        const pending = pendingRequests.current.get(requestId)!;
        clearTimeout(pending.timeout);
        pendingRequests.current.delete(requestId);

        if (type === CRM_MESSAGES.ERROR) {
          // GOTCHA G13: CRM sends { message }, not { error }
          pending.reject(new Error((payload as CrmErrorPayload).message));
        } else {
          pending.resolve(payload);
        }
        return; // Don't process as broadcast
      }

      // Handle broadcast messages (no requestId)
      switch (type) {
        case CRM_MESSAGES.READY:
          setIsReady(true);
          trackEvent({ event_type: 'iframe_focus', metadata: { iframe: 'crm' } });
          optionsRef.current.onReady?.();
          break;

        case CRM_MESSAGES.ROUTE_CHANGE: {
          const routeData = payload as CrmRouteChangePayload;
          trackEvent({ event_type: 'iframe_focus', page_path: routeData.path, metadata: { iframe: 'crm', title: routeData.title } });
          optionsRef.current.onRouteChange?.(routeData.path, routeData.title);
          break;
        }

        case CRM_MESSAGES.ERROR:
          // GOTCHA G13: CRM sends { message }, not { error }
          optionsRef.current.onError?.((payload as CrmErrorPayload).message);
          break;

        case CRM_MESSAGES.STATE:
          // STATE messages are only responses to GET_STATE requests
          // They should have requestId and be handled above
          console.warn("[CrmEmbed] Received STATE without requestId");
          break;

        case CRM_MESSAGES.NAVIGATE_PARENT: {
          const navData = payload as CrmNavigateParentPayload;
          optionsRef.current.onNavigateParent?.(navData.action);
          break;
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      // GOTCHA G3: Clear all pending requests on unmount
      pendingRequests.current.forEach((pending) => {
        clearTimeout(pending.timeout);
        pending.reject(new Error("Component unmounted"));
      });
      pendingRequests.current.clear();
    };
  }, []); // Empty deps - uses refs for callbacks

  // Reset state when needed
  const reset = useCallback(() => {
    setIsReady(false);
    // GOTCHA G3: Clear pending requests on reset
    pendingRequests.current.forEach((pending) => {
      clearTimeout(pending.timeout);
      pending.reject(new Error("Reset called"));
    });
    pendingRequests.current.clear();
  }, []);

  // Public API methods
  const navigate = useCallback(
    (path: string) => {
      sendMessage(CRM_MESSAGES.NAVIGATE, { path });
    },
    [sendMessage]
  );

  const getState = useCallback(async (): Promise<CrmState> => {
    return sendRequest<CrmState>(CRM_MESSAGES.GET_STATE);
  }, [sendRequest]);

  return {
    isReady,
    navigate,
    getState,
    reset,
  };
}
